import { Column, ColumnType, FieldType } from '../models/model'
import { formatBooleanToYesNo, formatDate, formatDateToDDMMYYYY } from '../services/common/common.service'
import { GlobalConstants } from './global-constants'
import {
  CASE_CLOSED,
  COMPLETED,
  IN_PROGRESS,
  QUERIES_RECEIVED,
  StatsCard,
  SUBMITTED,
  TASK_INITIATED,
  WAITING_ON_CLIENT,
  WAITING_ON_DRAFT
} from './stats-cards'
import { dateValidator } from '../validators/validators.validator'

export const COMPANY_ENTITY_DISPLAY_ID: Column = {
  name: 'Display ID',
  key: 'companyEntityDisplayId',
  type: ColumnType.STRING,
  sortByKey: 'companyEntityId',
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[COMPANY_ENTITY_DISPLAY_ID.key]
}

export const ENTITY_ID_WITH_FILTER: Column = {
  ...COMPANY_ENTITY_DISPLAY_ID,
  filter: {
    key: 'companyEntityDisplayId',
    type: FieldType.TEXT
  },
  cssClasses: ['rowColor', 'min-width-100']
}

export const ACTIVITY_ID: Column = {
  name: 'Activity ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  cssClasses: ['rowColor'],
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: data => `/${GlobalConstants.ROUTING.ACTIVITY}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[ACTIVITY_ID.key]
}

export const PENDING_ACTIVITY_ID: Column = {
  name: 'Activity ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ACTIVITY_ID.key]
}

export const ACTIVITY_NAME_WITH_TYPE_INDICATOR: Column = {
  name: 'Activity Name',
  key: 'name',
  type: ColumnType.HTML,
  build: (data: any) => {
    const activityName = data?.[ACTIVITY_NAME_WITH_TYPE_INDICATOR.key]
    return data['activityType'] === GlobalConstants.TASK_TYPE.ONLINE
      ? `<span class="act"></span> ${activityName}`
      : data['activityType'] === GlobalConstants.TASK_TYPE.OFFLINE
      ? `<span class="dis"></span> ${activityName}`
      : activityName
  }
}

export const ACTIVITY_NAME: Column = {
  name: 'Activity Name',
  key: 'name',
  type: ColumnType.STRING,
  sortByKey: 'name',
  cssClasses: ['rowColor'],
  filter: {
    key: 'name',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ACTIVITY_NAME.key]
}

export const EMPLOYEE_ID: Column = {
  name: 'Employee ID',
  key: 'employeeCode',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[EMPLOYEE_ID.key]
}

export const REQUEST_INITIATED_ON: Column = {
  name: 'Initiated On',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  sortByKey: 'createdDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'createdDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[REQUEST_INITIATED_ON.key])
}

export const DAYS_ELAPSED: Column = {
  name: 'Days Elapsed',
  key: 'daysElapsed',
  type: ColumnType.NUMBER,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[DAYS_ELAPSED.key]
}

export const RESPONSE_DUE_BY: Column = {
  name: 'Response Due By',
  key: 'responseDueDatetime',
  type: ColumnType.STRING,
  sortByKey: 'responseDueDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'responseDueDatetime',
    type: FieldType.DATE
  },
  build: (data: any) => formatDate(data?.[RESPONSE_DUE_BY.key], GlobalConstants.DATE_FORMAT['DD/MM/YYYY'])
}

export const TASK_CURRENT_STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'statusList',
    type: FieldType.SELECT,
    getOptionsList: module => {
      let options: StatsCard[]
      if (module == 'ACTIVITY') {
        options = GlobalConstants.ACTIVITY_STATUS_LIST
      } else if (module == 'QUERY') {
        options = [QUERIES_RECEIVED, IN_PROGRESS, COMPLETED]
      } else if (module == 'GB_TASK') {
        options = [QUERIES_RECEIVED, IN_PROGRESS, COMPLETED]
      }
      const optionsMap = new Map<string, string>()
      options.forEach(option => optionsMap.set(option.key, option.label))

      return optionsMap
    }
  },
  build: (data: any) => data?.[TASK_CURRENT_STATUS.key]
}

export const ASSIGNED_TO: Column = {
  name: 'Assigned To',
  key: 'assignedToDisplayId',
  type: ColumnType.STRING,
  sortByKey: 'assignedTo',
  cssClasses: ['rowColor'],
  filter: {
    key: 'assignedTo',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ASSIGNED_TO.key]
}

export const QUERY_ID: Column = {
  name: 'Query ID',
  key: 'displayId',
  type: ColumnType.STRING,
  build: (data: any) => data?.[QUERY_ID.key]
}

export const QUERY_NAME: Column = {
  name: 'Query Name',
  key: 'title',
  type: ColumnType.STRING,
  build: (data: any) => data?.[QUERY_NAME.key]
}

export const RAISED_ON: Column = {
  name: 'Raised On',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  build: (data: any) => formatDateToDDMMYYYY(data?.[RAISED_ON.key])
}

export const TASK_NAME: Column = {
  name: 'Task Name',
  key: 'name',
  type: ColumnType.STRING,
  sortByKey: 'name',
  cssClasses: ['rowColor'],
  filter: {
    key: 'name',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[TASK_NAME.key]
}

export const TASK_ID: Column = {
  name: 'Task ID',
  key: 'displayId',
  type: ColumnType.STRING,
  build: (data: any) => data?.[TASK_ID.key],
  sortByKey: 'displayId',
  cssClasses: ['rowColor'],
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: data => `/${GlobalConstants.ROUTING.GOVERNMENT_BUSINESS}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  }
}

export const OFFICE_NAME: Column = {
  name: 'Department & Office Name',
  key: 'department',
  key2: 'office',
  type: ColumnType.STRING,
  build: (data: any) => data?.[OFFICE_NAME.key] + ' - ' + data?.[OFFICE_NAME.key2]
}

export const RECEIVED_ON: Column = {
  name: 'Received on',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  sortByKey: 'createdDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'createdDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDate(data?.[RECEIVED_ON.key], GlobalConstants.DATE_FORMAT['DD/MM/YYYY'])
}

export const STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  build: (data: any) => data?.[STATUS.key],
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'status',
    type: FieldType.TEXT
  }
}

export const HEADING: Column = {
  name: 'Article Title',
  key: 'heading',
  type: ColumnType.STRING,
  cssClasses: ['knowledge-title'],
  sortByKey: 'heading',
  filter: {
    key: 'heading',
    type: FieldType.TEXT
  },
  anchor: {
    link: data => `/${GlobalConstants.ROUTING.KNOWLEDGE_HUB}/${data.id}/${GlobalConstants.ROUTING.VIEW}`
  },
  build: (data: any) => data?.[HEADING.key]
}

export const KNOWLEDGE_CATEGORY: Column = {
  name: 'Category',
  key: 'category',
  type: ColumnType.STRING,
  sortByKey: 'category',
  filter: {
    key: 'category',
    type: FieldType.SELECT,
    getOptionsList: category => {
      return []
    }
  },
  build: (data: any) => data?.[KNOWLEDGE_CATEGORY.key]
}

export const ANNOUNCEMENT_SUB_CATEGORY: Column = {
  name: 'Sub Category',
  key: 'taskSubCategory',
  type: ColumnType.STRING,
  sortByKey: 'taskSubCategory',
  filter: {
    key: 'taskSubCategory',
    type: FieldType.SELECT,
    getOptionsList: category => {
      return []
    }
  },
  build: (data: any) => data?.[ANNOUNCEMENT_SUB_CATEGORY.key]
}

export const VIEW_FOR_EMPLOYEE: Column = {
  name: 'Viewable by Employee',
  key: 'displayForEmployee',
  type: ColumnType.STRING,
  sortByKey: 'displayForEmployee',
  filter: {
    key: 'displayForEmployee',
    type: FieldType.SELECT,
    getOptionsList: module => {
      let options = ['YES', 'NO']
      return options
    }
  },
  build: (data: any) => formatBooleanToYesNo(data?.[VIEW_FOR_EMPLOYEE.key])
}

export const VIEW_FOR_ADMIN: Column = {
  name: 'Viewable by Admins',
  key: 'displayForAdmin',
  type: ColumnType.STRING,
  sortByKey: 'displayForAdmin',
  filter: {
    key: 'displayForAdmin',
    type: FieldType.SELECT,
    getOptionsList: module => {
      let options = ['YES', 'NO']
      return options
    }
  },
  build: (data: any) => formatBooleanToYesNo(data?.[VIEW_FOR_ADMIN.key])
}

export const ID: Column = {
  name: 'ID',
  key: 'employeeCode',
  type: ColumnType.STRING,
  sortByKey: 'employeeCode',
  filter: {
    key: 'employeeCode',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ID.key]
}

export const NAME: Column = {
  name: 'Name',
  key: 'firstName',
  type: ColumnType.STRING,
  sortByKey: 'firstName',
  filter: {
    key: 'firstName',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[NAME.key]
}

export const DOB: Column = {
  name: 'DOB',
  key: 'dob',
  type: ColumnType.STRING,
  sortByKey: 'dob',
  filter: {
    key: 'dob',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[DOB.key])
}

export const JOINING_DATE: Column = {
  name: 'Joining Date',
  key: 'doj',
  type: ColumnType.STRING,
  sortByKey: 'doj',
  filter: {
    key: 'doj',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[JOINING_DATE.key])
}

export const DESIGNATION: Column = {
  name: 'Designation',
  key: 'designation',
  type: ColumnType.STRING,
  filter: {
    key: 'designation',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.employeeEmploymentBasicDetails?.[DESIGNATION.key]
}

export const DEPARTMENT: Column = {
  name: 'Department',
  key: 'departmentName',
  type: ColumnType.STRING,
  filter: {
    key: 'departmentName',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.employeeEmploymentBasicDetails?.[DEPARTMENT.key]
}

export const LOCATION: Column = {
  name: 'Location',
  key: 'location',
  type: ColumnType.STRING,
  filter: {
    key: 'location',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.employeeEmploymentBasicDetails?.[LOCATION.key]
}

export const EMP_STATUS: Column = {
  name: 'Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  filter: {
    key: 'status',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[EMP_STATUS.key]
}

export const GOVT_DEPARTMENT: Column = {
  name: 'Department & Office Name',
  key: 'department',
  type: ColumnType.STRING,
  sortByKey: 'department',
  cssClasses: ['rowColor'],
  filter: {
    key: 'department',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[GOVT_DEPARTMENT.key]
}

export const CREATED: Column = {
  name: 'Created by',
  key: 'createdByDisplayId',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[CREATED.key]
}

export const QUERIES_ID: Column = {
  name: 'Query ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  cssClasses: ['rowColor'],
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: data => `/${GlobalConstants.ROUTING.QUERY}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[QUERIES_ID.key]
}

export const QUERY_EMPLOYEE_ID: Column = {
  name: 'Employee ID',
  key: 'employeeCode',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[QUERY_EMPLOYEE_ID.key]
}

export const QUERY_TITLE_NAME: Column = {
  name: 'Query Name',
  key: 'title',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  sortByKey: 'title',
  filter: {
    key: 'title',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[QUERY_TITLE_NAME.key]
}

export const QUERY_REQUEST_INITIATED: Column = {
  name: 'Request Initiated on',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  sortByKey: 'createdDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'createdDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[QUERY_REQUEST_INITIATED.key])
}

export const QUERY_DAYS_ELAPSED: Column = {
  name: 'Days Elapsed',
  key: 'daysElapsed',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[QUERY_DAYS_ELAPSED.key]
}

export const QUERY_RESPONSE_DUE: Column = {
  name: 'Response Due by',
  key: 'responseDueDatetime',
  type: ColumnType.STRING,
  sortByKey: 'responseDueDatetime',
  cssClasses: ['rowColor'],
  filter: {
    key: 'responseDueDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[QUERY_RESPONSE_DUE.key])
}

export const QUERY_STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'status',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[QUERY_STATUS.key]
}

export const ANNOUNCEMENT_ENTRY_ID: Column = {
  name: 'ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: data => `/${GlobalConstants.ROUTING.ANNOUNCEMENT}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[ANNOUNCEMENT_ENTRY_ID.key]
}

export const ANNOUNCEMENT_HEADING: Column = {
  name: 'Heading',
  key: 'heading',
  type: ColumnType.STRING,
  sortByKey: 'heading',
  filter: {
    key: 'heading',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ANNOUNCEMENT_HEADING.key]
}

export const ANNOUNCEMENT_DESCRIPTION: Column = {
  name: 'Description',
  key: 'description',
  type: ColumnType.STRING,
  sortByKey: 'description',
  filter: {
    key: 'description',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ANNOUNCEMENT_DESCRIPTION.key]
}

export const ANNOUNCEMENT_FROM: Column = {
  name: 'From',
  key: 'displayFromDatetime',
  type: ColumnType.STRING,
  sortByKey: 'displayFromDatetime',
  filter: {
    key: 'displayFromDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[ANNOUNCEMENT_FROM.key])
}

export const ANNOUNCEMENT_TO: Column = {
  name: 'To',
  key: 'displayToDatetime',
  type: ColumnType.STRING,
  sortByKey: 'displayToDatetime',
  filter: {
    key: 'displayToDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[ANNOUNCEMENT_TO.key])
}

export const ANNOUNCEMENT_STATUS: Column = {
  name: 'Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  filter: {
    key: 'status',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ANNOUNCEMENT_STATUS.key]
}

export const ANNOUNCEMENT_TYPE: Column = {
  name: 'Type',
  key: 'type',
  type: ColumnType.STRING,
  sortByKey: 'type',
  filter: {
    key: 'type',
    type: FieldType.SELECT,
    getOptionsList: module => {
      let options = ['NOTIFICATION', 'CIRCULAR']
      return options
    }
  },
  build: (data: any) => data?.[ANNOUNCEMENT_TYPE.key]
}

export const CALENDAR_DISPLAY_ID: Column = {
  name: 'ID',
  key: 'displayId',
  type: ColumnType.STRING,
  sortByKey: 'displayId',
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: data => `/${GlobalConstants.ROUTING.CALENDAR_INFO}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[CALENDAR_DISPLAY_ID.key]
}

export const CREATED_ON: Column = {
  name: 'Created on',
  key: 'createdDatetime',
  type: ColumnType.STRING,
  sortByKey: 'createdDatetime',
  filter: {
    key: 'createdDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[CREATED_ON.key])
}

export const CALENDAR_FROM: Column = {
  name: 'Display From',
  key: 'displayFromDatetime',
  type: ColumnType.STRING,
  sortByKey: 'displayFromDatetime',
  filter: {
    key: 'displayFromDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[CALENDAR_FROM.key])
}
export const CALENDAR_TO: Column = {
  name: 'Display Till',
  key: 'displayToDatetime',
  type: ColumnType.STRING,
  sortByKey: 'displayToDatetime',
  filter: {
    key: 'displayToDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[CALENDAR_TO.key])
}
export const CALENDAR_TASK_DATE: Column = {
  name: 'Task Date',
  key: 'dueDatetime',
  type: ColumnType.STRING,
  sortByKey: 'dueDatetime',
  filter: {
    key: 'dueDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[CALENDAR_TASK_DATE.key])
}
export const CALENDAR_DISPLAY_FOR: Column = {
  name: 'Display for',
  key: 'displayFor',
  type: ColumnType.STRING,
  sortByKey: 'displayFor',
  filter: {
    key: 'displayFor',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[CALENDAR_DISPLAY_FOR.key]
}

export const OWNER_DISPLAY_ID: Column = {
  name: 'Employee ID',
  key: 'ownerDisplayId',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[OWNER_DISPLAY_ID.key]
}

export const OWNER_AUTOCOMPLETE_DISPLAY_ID: Column = {
  name: 'Employee ID',
  key: 'ownerDisplayId',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  filter: {
    key: 'ownerId',
    type: FieldType.TEXT_AUTOCOMPLETE
  },
  build: (data: any) => data?.[OWNER_DISPLAY_ID.key]
}

export const ASSIGNED_TO_DISPLAY_ID: Column = {
  name: 'Assigned To',
  key: 'assignedToDisplayId',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  filter: {
    key: 'assignedToDisplayId',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[ASSIGNED_TO_DISPLAY_ID.key]
}

export const EMP_STATUS_SELECT: Column = {
  name: 'Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  filter: {
    key: 'status',
    type: FieldType.SELECT,
    getOptionsList: module => {
      return [
        GlobalConstants.EMPLOYEE_STATUS.ACTIVE,
        GlobalConstants.EMPLOYEE_STATUS.INACTIVE,
        GlobalConstants.EMPLOYEE_STATUS.TERMINATED
      ]
    }
  },
  build: (data: any) => data?.[EMP_STATUS_SELECT.key]
}

export const GB_TASK_CURRENT_STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'statusList',
    type: FieldType.SELECT_TASK_STATUS,
    getOptionsList: () => {
      let options: StatsCard[]
      options = [TASK_INITIATED, WAITING_ON_CLIENT, WAITING_ON_DRAFT, SUBMITTED, CASE_CLOSED, IN_PROGRESS]
      const optionsMap = new Map<string, string>()
      options.forEach(option => optionsMap.set(option.key, option.label))
      return optionsMap
    }
  },
  build: (data: any) => data?.[TASK_CURRENT_STATUS.key]
}

export const QUERY_TASK_CURRENT_STATUS: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'statusList',
    type: FieldType.SELECT_TASK_STATUS,
    getOptionsList: () => {
      let options: StatsCard[]
      options = [QUERIES_RECEIVED, IN_PROGRESS, COMPLETED]
      const optionsMap = new Map<string, string>()
      options.forEach(option => optionsMap.set(option.key, option.label))
      return optionsMap
    }
  },
  build: (data: any) => data?.[QUERY_TASK_CURRENT_STATUS.key]
}

export const UAN_NUMBER: Column = {
  name: 'UAN Number',
  key: 'uanNumber',
  type: ColumnType.STRING,
  filter: {
    key: 'uanNumber',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.employeeStatutoryDetails?.[UAN_NUMBER.key]
}

export const AADHAR_NUMBER: Column = {
  name: 'Aadhaar Number',
  key: 'aadhaar',
  type: ColumnType.STRING,
  filter: {
    key: 'aadhaar',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.employeeStatutoryDetails?.[AADHAR_NUMBER.key]
}

export const PF_NUMBER: Column = {
  name: 'PF number',
  key: 'currentPFAccountNumber',
  type: ColumnType.STRING,
  filter: {
    key: 'currentPFAccountNumber',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.employeeStatutoryDetails?.[PF_NUMBER.key]
}

export const LAST_UPDATED_ON: Column = {
  name: 'Last Updated On',
  key: 'updatedDatetime',
  type: ColumnType.STRING,
  sortByKey: 'updatedDatetime',
  filter: {
    key: 'updatedDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[LAST_UPDATED_ON.key])
}

export const LAST_UPDATED_BY: Column = {
  name: 'Last Updated By',
  key: 'updatedByDisplayId',
  type: ColumnType.STRING,
  filter: {
    key: 'updatedByDisplayId',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[LAST_UPDATED_BY.key]
}

export const NEXT_INTERNAL_DUE_DATE: Column = {
  name: 'Next Internal Due Date',
  key: 'nextInternalDueDate',
  type: ColumnType.STRING,
  sortByKey: 'nextInternalDueDate',
  filter: {
    key: 'nextInternalDueDate',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[NEXT_INTERNAL_DUE_DATE.key])
}

export let QUERY_CATEGORY_SELECT: Column = {
  name: 'Query Category',
  key: 'queryCategory',
  type: ColumnType.STRING,
  sortByKey: 'queryCategory',
  filter: {
    key: 'queryCategory',
    type: FieldType.SELECT,
    getOptionsList: module => {
      return []
    }
  },
  build: (data: any) => {}
}

export let PRIORITY_LEVEL_SELECT: Column = {
  name: 'Priority Level',
  key: 'priority',
  type: ColumnType.STRING,
  sortByKey: 'priority',
  filter: {
    key: 'priority',
    type: FieldType.SELECT,
    getOptionsList: module => {
      return []
    }
  },
  build: (data: any) => {}
}

export let NEXT_ACTION_SELECT: Column = {
  name: 'Next Action',
  key: 'nextAction',
  type: ColumnType.STRING,
  sortByKey: 'nextAction',
  filter: {
    key: 'nextAction',
    type: FieldType.SELECT,
    getOptionsList: module => {
      return []
    }
  },
  build: (data: any) => data?.[NEXT_ACTION_SELECT.key]
}

export let QUERY_TAG_MULTISELECT: Column = {
  name: 'Tag',
  key: 'tag',
  type: ColumnType.STRING,
  sortByKey: 'tag',
  filter: {
    key: 'tagList',
    type: FieldType.MULTISELECT,
    placeholder: 'Enter Tags',
    getOptionsList: module => {
      return []
    }
  },
  build: (data: any) => data?.[QUERY_TAG_MULTISELECT.key]
}

export let QUERY_TASK_CURRENT_STATUS_MULTISELECT: Column = {
  name: 'Current Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  cssClasses: ['rowColor'],
  filter: {
    key: 'statusList',
    type: FieldType.MULTISELECT,
    placeholder: 'Enter Status',
    getOptionsList: module => {
      return []
    },
    showIncludeAndExclude: true
  },
  build: (data: any) => data?.[QUERY_TASK_CURRENT_STATUS_MULTISELECT.key]
}

export let ASSIGNED_TO_MULTISELECT: Column = {
  name: 'Assigned To',
  key: 'assignedTo',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  filter: {
    key: 'assignedToList',
    type: FieldType.MULTISELECT,
    placeholder: 'Enter Assignee',
    getOptionsList: module => {
      return []
    }
  },
  build: (data: any) => data?.[ASSIGNED_TO_MULTISELECT.key]
}

export const OT_DISPLAY_ID: Column = {
  name: 'Ticket ID',
  key: 'displayId',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  filter: {
    key: 'displayId',
    type: FieldType.TEXT
  },
  anchor: {
    link: (data: { id: any }) =>
      `/${GlobalConstants.ROUTING.OFFLINE_TRACKER}/${data.id}/${GlobalConstants.ROUTING.DETAILS}`
  },
  build: (data: any) => data?.[OT_DISPLAY_ID.key]
}

export const OT_RECEIVED_DATE: Column = {
  name: 'Received Date',
  key: 'receivedDate',
  type: ColumnType.STRING,
  sortByKey: 'receivedDate',
  filter: {
    key: 'receivedDate',
    type: FieldType.DATE
  },
  cssClasses: ['rowColor'],
  build: (data: any) => formatDate(data?.[OT_RECEIVED_DATE.key], GlobalConstants.DATE_FORMAT['DD/MM/YYYY'])
}

export const OT_UNIQUE_MEMBER_ID: Column = {
  name: 'Unique Member Id',
  key: 'memberId',
  filter: {
    key: 'memberId',
    type: FieldType.TEXT
  },
  type: ColumnType.STRING,
  build: (data: any) => data?.[OT_UNIQUE_MEMBER_ID.key]
}

export const OT_EE_NAME: Column = {
  name: 'EE Name',
  key: 'employeeFullname',
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => `${data?.['firstName']} ${data?.['middleName']} ${data?.['lastName']}`
}

export const OT_EE_ID: Column = {
  name: 'EE ID',
  key: 'employeeCode',
  filter: {
    key: 'employeeCode',
    type: FieldType.TEXT
  },
  type: ColumnType.STRING,
  cssClasses: ['rowColor'],
  build: (data: any) => data?.[OT_EE_ID.key]
}

export const OT_LAST_UPDATED_ON: Column = {
  name: 'Last Updated On',
  key: 'updatedDatetime',
  type: ColumnType.STRING,
  filter: {
    key: 'updatedDatetime',
    type: FieldType.DATE,
    validations: [{ message: 'Invalid Date', validator: dateValidator(), validationType: 'invalidDate' }]
  },
  build: (data: any) => formatDateToDDMMYYYY(data?.[LAST_UPDATED_ON.key])
}

export const KNOWLEDGE_BANK_STATUS_SELECT: Column = {
  name: 'Status',
  key: 'status',
  type: ColumnType.STRING,
  sortByKey: 'status',
  filter: {
    key: 'status',
    type: FieldType.SELECT,
    getOptionsList: module => GlobalConstants.KNOWLEDGE_BANK_STATUS_LIST
  },
  build: (data: any) => data?.[EMP_STATUS_SELECT.key]
}

export const TASK_CATEGORY: Column = {
  name: 'Category',
  key: 'taskCategory',
  type: ColumnType.STRING,
  sortByKey: 'taskCategory',
  filter: {
    key: 'taskCategory',
    type: FieldType.SELECT,
    getOptionsList: category => {
      return []
    }
  },
  build: (data: any) => data?.[TASK_CATEGORY.key]
}

export const TASK_SUB_CATEGORY: Column = {
  name: 'Sub Category',
  key: 'taskSubCategory',
  type: ColumnType.STRING,
  sortByKey: 'taskSubCategory',
  filter: {
    key: 'taskSubCategory',
    type: FieldType.SELECT,
    getOptionsList: category => {
      return []
    }
  },
  build: (data: any) => data?.[TASK_SUB_CATEGORY.key]
}

export const COMPANY_ENTITY_DISPLAY: Column = {
  name: 'Display ID',
  key: 'companyEntityId',
  type: ColumnType.STRING,
  sortByKey: 'companyEntityId',
  filter: {
    key: 'companyEntityId',
    type: FieldType.SELECT,
    getOptionsList: category => {
      return []
    }
  },
  build: (data: any) => data?.[COMPANY_ENTITY_DISPLAY.key]
}

export const BRANCH_CODE: Column = {
  name: 'Branch Code',
  key: 'location',
  type: ColumnType.STRING,
  filter: {
    key: 'location',
    type: FieldType.TEXT
  },
  build: (data: any) => data?.[BRANCH_CODE.key]
}

export const PREVIOUS_PF: Column = {
  name: 'Previous PF',
  key: 'havePfAccount',
  type: ColumnType.STRING,
  cssClasses: ['min-width-80'],
  build: (data: any) => {
    let result = '-'
    if (data.taskCode === 'EPF_NEW_JOINEE_WORKFLOW') {
      result = data?.[PREVIOUS_PF.key] === true ? 'Yes' : 'No'
    }
    return result
  }
}
